import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { CallOutcome, CallSheetSort, SortDirection, type CallFromSheetDto, type CallSheetRowDto, type ProspectBookRowDto } from '@signalvote/shared';
import { Check, ChevronLeft, MoreHorizontal, X } from 'lucide-react';
import { formatPhone } from '@/lib/phone';
import type { CallBookSearch } from '@/lib/fundraising/bookFilters';
import { booksByPerson } from '@/lib/fundraising/callSheetAdd';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { SelectCheckbox } from './SelectCheckbox';
import { SelectionBar, SelectionBarButton, SelectionBarDivider } from './SelectionBar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import {
  isCallSheetChangedElsewhere,
  useAddToCallSheet,
  useCallSheet,
  useCreateCallSheet,
  useDeleteCallSheet,
  useOrderCallSheet,
  usePrefetchProspectDetail,
  useProspectBook,
  useRemoveFromCallSheet,
  useRenameCallSheet,
} from '@/hooks/queries';
import { useDelayedFlag } from '@/hooks/useDelayedFlag';
import { getCampaignTodayString } from '@/lib/calendarUtils';
import { CALL_BOOK_DEFAULT_ORDER, callBookOrderLabel, nextSort, type ColumnSort } from '@/lib/fundraising/sorting';
import { cn } from '@/lib/utils';
import { LOADING_DELAY_MS, SortHeader, StaleRowsIndicator } from './SortHeader';
import { AddToCallSheetDialog } from './AddToCallSheetDialog';
import { CallSheetNameDialog, DeleteCallSheetDialog } from './CallSheetDialogs';
import { FundraisingPage } from './FundraisingPage';
import { dayOrClock, weekdayDay } from './activityDay';
import { keptHistoryState, openedFromList, ProspectProfileModal } from './ProspectProfileModal';
import { useHoverIntent, type HoverIntent } from './useHoverIntent';
import { useSelectionKeys } from './useSelectionKeys';
import { useWindowedRows } from './useWindowedRows';
import { ColumnWidths, STICKY_HEADING } from './WindowedTable';

const dollars = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const count = new Intl.NumberFormat('en-US');

const SHEET = '/admin/fundraising/call-books/$sheetId';

/** The select box and the order columns — what a spacer row spans. */
const COLUMNS = 7;

const OUTCOME: Record<CallOutcome, string> = {
  [CallOutcome.NoAnswer]: 'No answer',
  [CallOutcome.Voicemail]: 'Left voicemail',
  [CallOutcome.CallBackRequested]: 'Call back',
  [CallOutcome.Committed]: 'Committed',
  [CallOutcome.Declined]: 'Declined',
  [CallOutcome.WrongNumber]: 'Wrong number',
  [CallOutcome.RemovalRequested]: 'Asked not to be called',
};

/**
 * What came of a person's last call from this book — never a call from anywhere else: its outcome ("Called,
 * no outcome" when the dial was never logged) with what it committed, then its campaign-local day or clock, with the day a call back
 * was asked for. "Not called" when nobody has called them from it.
 */
function FromThisBookCell({ call, today }: { call: CallFromSheetDto | null; today: string }) {
  if (!call) return <span className="text-[13px] text-muted-foreground">Not called</span>;
  // Warning only for what's owed: a call back, or a commitment to chase.
  const owed = call.outcome === CallOutcome.CallBackRequested || call.outcome === CallOutcome.Committed;
  const when = [...(call.callBackOn ? [`Asked for ${weekdayDay(call.callBackOn)}`] : []), dayOrClock(call.calledOn, call.calledAt, today)];
  return (
    <span className="block whitespace-nowrap">
      <span className={cn('block text-[13px]', owed ? 'text-warning' : 'text-foreground')}>
        {call.outcome === null ? 'Called, no outcome' : OUTCOME[call.outcome]}
        {call.commitmentAmount !== null && ` ${dollars.format(call.commitmentAmount)}`}
      </span>
      <span className="block text-xs text-muted-foreground">{when.join(' · ')}</span>
    </span>
  );
}


/**
 * The book's columns, each of which orders it: "#" is when each person was added. Each has its fixed width (null
 * takes the rest) — a four-digit place, a phone number, the heading "Ask when added", a call's outcome and day,
 * and the other books' names, which truncate.
 */
const ORDER_COLUMNS: { column: CallSheetSort; label: string; width: string | null; className?: string; hint?: string }[] = [
  { column: CallSheetSort.Added, label: '#', width: 'w-14', className: 'px-2 text-right', hint: 'Order by when each person was added. # is always their place in the calling order.' },
  { column: CallSheetSort.Name, label: 'Person', width: null },
  { column: CallSheetSort.Phone, label: 'Phone', width: 'w-36' },
  { column: CallSheetSort.Ask, label: 'Ask when added', width: 'w-40', className: 'text-right' },
  { column: CallSheetSort.FromThisBook, label: 'From this book', width: 'w-44' },
  { column: CallSheetSort.AlsoOn, label: 'Also on', width: 'w-44' },
];
const COLUMN_WIDTHS = ['w-11', ...ORDER_COLUMNS.map(({ width }) => width)];

/** One line of the book: its row (with the frozen Ask) and the person as the Prospects book reads them. */
type Line = { row: CallSheetRowDto; person: ProspectBookRowDto | undefined };

/**
 * One call book, with the person open over it (`person`, in the URL): their profile, stepping through the
 * book in the order it's shown. `sort`/`dir` (also in the URL) are an order the Fundraiser is only viewing it
 * in; without them it's the saved order, the one Callers get.
 */
export function CallSheet({ sheetId, person, sort, dir }: { sheetId: string; person?: string } & Omit<CallBookSearch, 'person'>) {
  const navigate = useNavigate();
  const viewSearch = useMemo(() => ({ ...(sort && { sort }), ...(dir && { dir }) }), [sort, dir]);
  const open = useCallback(
    (id: string) => void navigate({ to: SHEET, params: { sheetId }, search: { ...viewSearch, person: id }, state: openedFromList }),
    [navigate, sheetId, viewSearch],
  );
  const changeView = useCallback(
    (next: ColumnSort<CallSheetSort> | null) =>
      void navigate({
        to: SHEET,
        params: { sheetId },
        search: next ? { sort: next.column, dir: next.descending ? SortDirection.Desc : SortDirection.Asc } : {},
        replace: true,
      }),
    [navigate, sheetId],
  );
  // The same reads as the book's page (one cache entry each): its lines' order as shown, and every book for the profile.
  const { data: detail } = useCallSheet(sheetId, { sort: sort ?? null, dir: dir ?? null });
  const { data: book } = useProspectBook({ sheetId });
  const order = useMemo(() => (detail?.rows ?? []).map((row) => row.prospectId), [detail?.rows]);
  return (
    <>
      <CallBookPage sheetId={sheetId} sort={sort} dir={dir} onOpen={open} onViewChange={changeView} />
      {person && (
        <ProspectProfileModal
          prospectId={person}
          order={order}
          total={order.length}
          sheets={book?.sheets ?? []}
          onStep={(id) =>
            void navigate({ to: SHEET, params: { sheetId }, search: { ...viewSearch, person: id }, replace: true, state: keptHistoryState })
          }
          onDismiss={() => void navigate({ to: SHEET, params: { sheetId }, search: viewSearch, replace: true })}
        />
      )}
    </>
  );
}

const sameOrder = (a: ColumnSort<CallSheetSort>, b: ColumnSort<CallSheetSort>) => a.column === b.column && a.descending === b.descending;

/**
 * One call book. It opens in the order saved on it — the order Callers work it in (largest suggested ask
 * when each person was added, by default). Sorting a column only changes the view (in the URL, sorted by the
 * server — the table never re-sorts rows itself); "Save as calling order" makes the viewed order the one
 * Callers get, after a warning. Select people to take them off (never deleting a call) or add them to another
 * book; rename or delete it. Each person's status is what came of their last call from this book. A name (or
 * anywhere on its row) opens that person over the book. Memoized, so opening or stepping through people
 * redraws only the dialog, never this table.
 */
const CallBookPage = memo(function CallBookPage({
  sheetId,
  sort,
  dir,
  onOpen,
  onViewChange,
}: {
  sheetId: string;
  sort?: CallSheetSort;
  dir?: SortDirection;
  onOpen: (prospectId: string) => void;
  /** Show the book in an order, or (null) back in its saved one. */
  onViewChange: (next: ColumnSort<CallSheetSort> | null) => void;
}) {
  const navigate = useNavigate();
  // A person the pointer rests on is read ahead of time, so opening them shows them at once.
  const hover = useHoverIntent(usePrefetchProspectDetail());
  const { data: detail, isLoading, isError, isPlaceholderData } = useCallSheet(sheetId, { sort: sort ?? null, dir: dir ?? null });
  const { data: book } = useProspectBook({ sheetId });
  const { mutate: removeFrom, error: removeFromError, reset: resetRemoveFrom } = useRemoveFromCallSheet();
  const { mutate: addTo, error: addError, reset: resetAdd } = useAddToCallSheet();
  const { mutate: create, error: createError, reset: resetCreate } = useCreateCallSheet();
  const { mutate: rename, error: renameError, reset: resetRename } = useRenameCallSheet();
  const { mutate: remove, error: removeError, reset: resetRemove } = useDeleteCallSheet();
  const { mutate: order, error: orderError, reset: resetOrder, isPending: ordering } = useOrderCallSheet();
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [adding, setAdding] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [notes, setNotes] = useState<string[]>([]);
  const today = getCampaignTodayString();

  const membership = book?.sheets.find((sheet) => sheet.id === sheetId);
  const progress = book?.sheet?.id === sheetId ? book.sheet : null;
  const people = useMemo(() => new Map((book?.rows ?? []).map((row) => [row.prospect.id, row])), [book?.rows]);
  const lines: Line[] = useMemo(() => (detail?.rows ?? []).map((row) => ({ row, person: people.get(row.prospectId) })), [detail?.rows, people]);
  const otherBooks = useMemo(() => (book?.sheets ?? []).filter((sheet) => sheet.id !== sheetId), [book?.sheets, sheetId]);
  // Each person's other books, named once per read — a string, so a row's prop stays equal across renders.
  const alsoOn = useMemo(
    () => new Map([...booksByPerson(otherBooks)].map(([id, books]) => [id, books.map((b) => b.name).join(', ')])),
    [otherBooks],
  );
  // Built once per change to the selection or the people, so the "Add to call book" preview isn't worked out again
  // on every redraw of the book.
  const selectedPeople = useMemo(
    () => [...selected].map((id) => people.get(id)).filter((p): p is ProspectBookRowDto => !!p),
    [selected, people],
  );
  const name = membership?.name ?? detail?.sheet.name ?? '';
  const changeError = (removeFromError ?? addError ?? createError)?.message ?? null;

  // Two orders: the one saved on the book (what Callers get on the phone) and the one it's shown in — the
  // saved one unless the Fundraiser sorted a column to look. The server sorts either; this table only shows
  // the rows as served.
  const saved: ColumnSort<CallSheetSort> = detail
    ? { column: detail.sheet.sortBy, descending: detail.sheet.sortDescending }
    : CALL_BOOK_DEFAULT_ORDER;
  const viewing: ColumnSort<CallSheetSort> | null =
    sort || dir ? { column: sort ?? CallSheetSort.Ask, descending: dir === SortDirection.Desc } : null;
  const shown = viewing ?? saved;
  const unsaved = viewing !== null && !sameOrder(viewing, saved);
  const [confirmingSave, setConfirmingSave] = useState(false);

  // A header click waits on the server's re-sort; it's settled once a book arrives that isn't stale.
  const [viewingBy, setViewingBy] = useState<CallSheetSort | null>(null);
  useEffect(() => {
    if (!isPlaceholderData) setViewingBy(null);
  }, [detail, isPlaceholderData]);
  // Dimmed while another view loads, or while a save waits for the book to be re-read.
  const showStale = useDelayedFlag(isPlaceholderData || ordering, LOADING_DELAY_MS);
  // Moving to another view leaves a failed save's message behind.
  const showView = (next: ColumnSort<CallSheetSort> | null) => {
    resetOrder();
    onViewChange(next);
  };
  const changeView = (column: CallSheetSort) => {
    if (ordering) return;
    const next = nextSort(shown, column, saved);
    setViewingBy(column);
    showView(sameOrder(next, saved) ? null : next);
  };
  const saveView = () => {
    if (!membership || !viewing) return;
    setConfirmingSave(false);
    resetOrder();
    order(
      {
        sheet: membership,
        sortBy: sameOrder(viewing, CALL_BOOK_DEFAULT_ORDER) ? null : viewing.column,
        sortDescending: viewing.descending,
      },
      {
        onSuccess: (sheet) => {
          onViewChange(null);
          setNotes([`Callers will now go in this order: ${callBookOrderLabel(sheet.sortBy, sheet.sortDescending)}`]);
        },
      },
    );
  };
  const orderMessage = !orderError
    ? null
    : isCallSheetChangedElsewhere(orderError)
      ? 'Someone else changed this book since you opened it, so the calling order wasn’t saved. It’s been reloaded — check the order and save again.'
      : orderError.message;

  const toggle = useCallback(
    (id: string) =>
      setSelected((was) => {
        const next = new Set(was);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      }),
    [],
  );
  const clear = useCallback(() => setSelected(new Set()), []);
  useSelectionKeys({
    onToggle: (id) => {
      if (people.has(id)) toggle(id);
    },
    onAdd: () => setAdding(true),
    onClear: clear,
    hasSelection: selected.size > 0,
    paused: adding || renaming || deleting,
  });
  const allSelected = lines.length > 0 && selected.size === lines.length;
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(lines.map((l) => l.row.prospectId)));

  // Windowed, as the Prospects book is, so a long book mounts only the rows in view. Where the rows start
  // moves with the header above them: its progress, the order lines, a note or an error.
  const { listRef, bodyRef, measure, rows: windowedRows } = useWindowedRows(lines.length, [
    detail,
    progress,
    unsaved,
    notes,
    changeError,
    orderMessage,
  ]);

  const renderLine = ({ row, person }: Line, index: number) => (
    <CallBookRow
      key={row.prospectId}
      row={row}
      person={person}
      index={index}
      measure={measure}
      isSelected={selected.has(row.prospectId)}
      alsoOn={alsoOn.get(row.prospectId)}
      today={today}
      onOpen={onOpen}
      hover={hover}
      onToggle={toggle}
    />
  );

  return (
    <FundraisingPage tab="call-books">
      <div className="space-y-4 pb-20">
        <Link
          to="/admin/fundraising/call-books"
          aria-label="Back to call books"
          className="inline-flex h-8 items-center gap-1 text-[13px] text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          Call books
        </Link>

        {isLoading && <Skeleton className="h-40 w-full" />}
        {isError && !isLoading && (
          <p className="text-sm text-muted-foreground">
            We couldn’t load that call book — it may have been deleted.{' '}
            <Link to="/admin/fundraising/call-books" className="underline">
              Back to call books
            </Link>
          </p>
        )}

        {detail && (
          <>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-bold text-foreground">{name}</h2>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={!membership}
                    onClick={() => {
                      resetRename();
                      setRenaming(true);
                    }}
                  >
                    Rename
                  </Button>
                </div>
                {progress && (
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                    <div aria-hidden="true" className="h-1.5 w-full max-w-80 overflow-hidden rounded-full bg-muted sm:w-80">
                      <div
                        className="h-full bg-primary"
                        style={{ width: `${progress.people ? Math.min(100, (progress.called / progress.people) * 100) : 0}%` }}
                      />
                    </div>
                    <p className="text-xs tabular-nums text-muted-foreground">
                      <span className="font-semibold text-foreground">
                        {count.format(progress.called)} of {count.format(progress.people)} called
                      </span>{' '}
                      · {count.format(Math.max(0, progress.people - progress.called))} to go ·{' '}
                      <span className={cn(progress.callBacksDue > 0 && 'font-semibold text-warning')}>
                        {count.format(progress.callBacksDue)} {progress.callBacksDue === 1 ? 'call-back' : 'call-backs'} owed
                      </span>
                    </p>
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  <span className="font-semibold text-foreground">Callers’ order: {callBookOrderLabel(saved.column, saved.descending)}</span>
                  {!unsaved && ' · Sort a column to look at the book another way; Callers’ order stays until you save one.'}
                </p>
                {unsaved && viewing && (
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                    <span className="rounded bg-muted px-1.5 py-0.5 text-muted-foreground">
                      Viewing: {callBookOrderLabel(viewing.column, viewing.descending)} — not saved
                    </span>
                    <Button size="sm" variant="outline" className="h-8" disabled={!membership || ordering} onClick={() => setConfirmingSave(true)}>
                      Save as calling order
                    </Button>
                    <button type="button" className="text-muted-foreground underline underline-offset-4 hover:text-foreground" onClick={() => showView(null)}>
                      Back to calling order
                    </button>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 text-[13px] text-muted-foreground">
                <span>
                  To add people,{' '}
                  <Link to="/admin/fundraising" className="underline underline-offset-4">
                    select them in Prospects
                  </Link>
                </span>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-11 w-11" aria-label={`More for ${name}`} disabled={!membership}>
                      <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      className="text-destructive"
                      onSelect={() => {
                        resetRemove();
                        setDeleting(true);
                      }}
                    >
                      Delete book…
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {changeError && <p className="text-sm text-destructive">{changeError}</p>}
            {orderMessage && <p className="text-sm text-destructive">{orderMessage}</p>}
            {notes.length > 0 && (
              <div role="status" className="flex items-center justify-between gap-2 rounded-lg border bg-card px-3 py-2 text-[13px]">
                <span>
                  <Check className="mr-1.5 inline h-4 w-4 text-success" />
                  {notes.join(' · ')}
                </span>
                <button type="button" aria-label="Dismiss" className="grid h-8 w-8 place-items-center" onClick={() => setNotes([])}>
                  <X className="h-4 w-4 text-muted-foreground" />
                </button>
              </div>
            )}

            {lines.length === 0 ? (
              <p className="rounded-xl border bg-card px-5 py-10 text-center text-[13px] text-muted-foreground">
                No one on this book yet. To add people, select them on the Prospects tab.
              </p>
            ) : (
              // Clipped, never a scroll box, so the headings pin to the page scroller (see WindowedTable). The columns'
              // widths add up to its least width; a narrower window scrolls the page sideways.
              <div ref={listRef} aria-busy={showStale} className="relative min-w-[62rem] overflow-clip rounded-xl border bg-card">
                <StaleRowsIndicator shown={showStale} />
                <table className="w-full table-fixed text-[13px]">
                  <ColumnWidths widths={COLUMN_WIDTHS} />
                  <thead className="whitespace-nowrap text-left text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                    <tr className="h-10">
                      <th className={cn('pl-3', STICKY_HEADING)}>
                        <SelectCheckbox
                          checked={allSelected ? true : selected.size > 0 ? 'indeterminate' : false}
                          onCheckedChange={toggleAll}
                          aria-label="Select everyone on this book"
                        />
                      </th>
                      {ORDER_COLUMNS.map(({ column, label, className, hint }) => (
                        <SortHeader
                          key={column}
                          label={label}
                          column={column}
                          sort={shown}
                          onSort={changeView}
                          disabled={ordering}
                          pending={isPlaceholderData && viewingBy === column}
                          className={cn(className, STICKY_HEADING)}
                          hint={hint}
                        />
                      ))}
                    </tr>
                  </thead>
                  <tbody ref={bodyRef} className={cn('transition-opacity motion-reduce:transition-none', showStale && 'opacity-60')}>
                    {windowedRows(COLUMNS, (index) => renderLine(lines[index], index))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {membership && (
          <SelectionBar count={selected.size}>
            <SelectionBarDivider />
            <SelectionBarButton
              onClick={() => {
                resetRemoveFrom();
                const ids = [...selected];
                removeFrom(
                  { sheet: membership, prospectIds: ids },
                  {
                    onSuccess: (result) => {
                      clear();
                      setNotes([`Took ${result.removed} off ${result.sheet.name} · their calls stay on their record`]);
                    },
                  },
                );
              }}
            >
              Remove from book
            </SelectionBarButton>
            <SelectionBarButton onClick={() => setAdding(true)}>Add to another book</SelectionBarButton>
            <SelectionBarButton shortcut="Esc" onClick={clear}>
              Clear
            </SelectionBarButton>
          </SelectionBar>
        )}
      </div>

      <AddToCallSheetDialog
        open={adding}
        onOpenChange={setAdding}
        selected={selectedPeople}
        books={otherBooks}
        onAdd={(sheet, ids) => {
          resetAdd();
          setNotes([]);
          addTo(
            { sheet, prospectIds: ids },
            { onSuccess: (result) => setNotes((was) => [...was, `Added ${result.added} to ${result.sheet.name}`]) },
          );
        }}
        onCreate={(newName, ids) => {
          resetCreate();
          setNotes([]);
          create(
            { name: newName, prospectIds: ids },
            { onSuccess: (result) => setNotes((was) => [...was, `Created ${result.sheet.name} with ${result.added}`]) },
          );
        }}
      />
      {membership && (
        <>
          <CallSheetNameDialog
            open={renaming}
            onOpenChange={setRenaming}
            title={`Rename ${membership.name}`}
            initialName={membership.name}
            submitLabel="Save"
            error={renameError?.message ?? null}
            onSubmit={(next) => rename({ sheet: membership, name: next }, { onSuccess: () => setRenaming(false) })}
          />
          {viewing && (
            <AlertDialog open={confirmingSave} onOpenChange={setConfirmingSave}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Save as the calling order?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Callers on the phone will go through {membership.name} in this order:{' '}
                    {callBookOrderLabel(viewing.column, viewing.descending)}. Anyone mid-session picks it up the next time their
                    list refreshes.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={saveView}>Save calling order</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          <DeleteCallSheetDialog
            open={deleting}
            onOpenChange={setDeleting}
            name={membership.name}
            size={progress?.people ?? membership.size}
            error={removeError?.message ?? null}
            onConfirm={() =>
              remove(
                { sheet: membership },
                {
                  onSuccess: () => {
                    setDeleting(false);
                    void navigate({ to: '/admin/fundraising/call-books' });
                  },
                },
              )
            }
          />
        </>
      )}
    </FundraisingPage>
  );
});

/**
 * One person's line on the book: 56px, two lines. Memoised: every prop is stable unless this line changed, so
 * ticking one person or selecting everyone re-renders just the rows involved, not the whole book.
 */
const CallBookRow = memo(function CallBookRow({
  row,
  person,
  index,
  measure,
  isSelected,
  alsoOn,
  today,
  onOpen,
  hover,
  onToggle,
}: {
  row: CallSheetRowDto;
  person: ProspectBookRowDto | undefined;
  /** Their place in the book as shown, from 0. */
  index: number;
  /** The virtualizer's measuring ref, when the book is windowed. */
  measure: ((el: Element | null) => void) | undefined;
  isSelected: boolean;
  /** The other books they're on, named; undefined when none. */
  alsoOn: string | undefined;
  today: string;
  onOpen: (prospectId: string) => void;
  hover: HoverIntent;
  onToggle: (prospectId: string) => void;
}) {
  // A blank one reads as the server filled it from their FEC record, unmarked here.
  const meta = person && (person.workLine.employer.value ?? person.workLine.occupation.value);
  return (
    <tr
      ref={measure}
      data-index={index}
      data-prospect-id={row.prospectId}
      className={cn('h-14 cursor-pointer border-b last:border-0 hover:bg-muted/40', isSelected && 'bg-primary/5')}
      onClick={() => onOpen(row.prospectId)}
      onMouseEnter={() => hover.enter(row.prospectId)}
      onMouseLeave={hover.leave}
    >
      <td className="pl-3" onClick={(e) => e.stopPropagation()}>
        <SelectCheckbox
          checked={isSelected}
          onCheckedChange={() => onToggle(row.prospectId)}
          aria-label={`Select ${row.name}`}
          disabled={!person}
        />
      </td>
      <td className="truncate px-2 text-right tabular-nums text-muted-foreground">{index + 1}</td>
      <td className="px-3 py-2">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onOpen(row.prospectId);
          }}
          className="block max-w-full truncate text-left font-semibold text-foreground hover:underline"
        >
          {row.name}
        </button>
        {meta && <span className="block truncate text-xs text-muted-foreground">{meta}</span>}
      </td>
      <td className="truncate px-3 tabular-nums">{formatPhone(person?.prospect.phone)}</td>
      <td className="truncate px-3 text-right font-semibold tabular-nums">
        {row.ask == null ? <span className="font-normal text-muted-foreground">—</span> : dollars.format(row.ask)}
      </td>
      <td className="overflow-hidden whitespace-nowrap px-3 py-2">
        <FromThisBookCell call={row.fromThisBook} today={today} />
      </td>
      <td className="truncate px-3 text-muted-foreground" title={alsoOn ?? undefined}>
        {alsoOn ?? '—'}
      </td>
    </tr>
  );
});
